/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dropdown } from 'sharedUi/Dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { Toast } from 'sharedUi/Toast';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useDebounce } from '../../../hooks/useDebounce';
import { sortRows } from '../../../utils/client-sort';
import { permissionsApi } from '../../../services/rbac.service';
import { projectsService, type AccessibleProject } from '../../../services/projects.service';
import { ProjectCell } from '../../../components/common/project-cell';
import type {
  Permission,
  PermissionCreateRequest,
  PermissionUpdateRequest,
  Scope,
  RbacStatus,
} from '../../../types/rbac';
import DependencyDeleteDialog from '../../../components/rbac/dependency-delete-dialog';
import EntityLinksDialog from '../../../components/rbac/entity-links-dialog';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../../components/common/saas-data-table';
import {
  StatusTag,
  DetailShell,
  DetailCard,
  DetailRow,
  FormShell,
  SCOPE_OPTIONS,
  scopeLabel,
  useRbacToast,
  Field,
  TextField,
  TextAreaField,
} from './shared';
import { useHasPermission } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [{ field: 'displayName', header: 'Display Name', filter: true }];

interface FormState {
  key: string;
  displayName: string;
  scope: Scope;
  status: RbacStatus;
  description: string;
  appId: string;
}

const EMPTY: FormState = {
  key: '',
  displayName: '',
  scope: 'BOTH',
  status: 'ACTIVE',
  description: '',
  appId: '',
};

const PermissionsTab = () => {
  const queryClient = useQueryClient();
  const { toast, showSuccess, showError } = useRbacToast();
  const canCreate = useHasPermission(PERMISSIONS.AUTH_PERMISSION_CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.AUTH_PERMISSION_UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.AUTH_PERMISSION_DELETE);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // In-page (client-side) sort of the currently-loaded page. Search + filters stay server-side.
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));
  // "Show global/core rows" toggle. Only has an effect when the deployment enables
  // SCOPE_PERMISSION_CATALOG (consumers who mint their own permissions); no-op for core.
  const [showGlobal, setShowGlobal] = useState(false);
  // Project filter — a project shows its own permissions + the global ones.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewRow, setViewRow] = useState<Permission | null>(null);
  const [linksRow, setLinksRow] = useState<Permission | null>(null);

  const page = Math.floor(first / pageSize) + 1;
  const filtersParam = buildColumnFiltersParam(filters);

  // Projects the caller may access — drives the form's Project dropdown.
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });

  const appIdParam = selectedProjectId || undefined;
  const { data, isFetching } = useQuery({
    queryKey: ['rbac', 'permissions', debouncedSearch, page, pageSize, filtersParam, showGlobal, appIdParam],
    queryFn: () =>
      permissionsApi.list({
        search: debouncedSearch || undefined,
        page,
        page_size: pageSize,
        filters: filtersParam,
        include_global: showGlobal,
        app_id: appIdParam,
      }),
  });

  const handleFilter = (event: any) => {
    setFilters(event.filters || {});
    setFirst(0);
  };

  const rows = data?.data || [];
  const displayRows = useMemo(() => sortRows(rows, sortField, sortOrder), [rows, sortField, sortOrder]);
  const total = data?.pagination?.totalItems ?? (data?.pagination as any)?.total ?? 0;

  const setField = (k: keyof FormState, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row: Permission) => {
    setEditId(row._id);
    setForm({
      key: row.key,
      displayName: row.displayName || '',
      scope: row.scope || 'BOTH',
      status: row.status || 'ACTIVE',
      description: row.description || '',
      appId: row.appId || '',
    });
    setErrors({});
    setDialogOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['rbac', 'permissions'] });

  const createMut = useMutation({
    mutationFn: (payload: PermissionCreateRequest) => permissionsApi.create(payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Permission created');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to create permission'),
  });

  const updateMut = useMutation({
    mutationFn: (payload: PermissionUpdateRequest) => permissionsApi.update(editId!, payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Permission updated');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to update permission'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => permissionsApi.remove(id),
    onSuccess: () => {
      invalidate();
      showSuccess('Permission deleted');
      setDeleteId(null);
    },
    onError: (e) => {
      showError(e, 'Failed to delete permission');
      setDeleteId(null);
    },
  });

  const saving = createMut.isPending || updateMut.isPending;

  const validate = () => {
    const err: Record<string, string> = {};
    if (!form.key.trim()) err.key = 'Key is required';
    if (!form.displayName.trim()) err.displayName = 'Display name is required';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const save = () => {
    if (!validate()) return;
    if (editId) {
      updateMut.mutate({
        displayName: form.displayName,
        description: form.description || undefined,
        scope: form.scope,
        status: form.status,
      });
    } else {
      createMut.mutate({
        key: form.key.trim(),
        displayName: form.displayName,
        description: form.description || undefined,
        scope: form.scope,
        status: form.status,
        appId: form.appId || undefined,
      });
    }
  };

  const columns: SaasColumn<Permission>[] = useMemo(
    () => [
      { field: 'key', header: 'Key', sortable: true, filter: true, hideable: false, body: (r) => <span className="font-mono text-xs">{r.key}</span> },
      { field: 'displayName', header: 'Display Name', sortable: true, filter: true },
      { field: 'appId', header: 'Project', sortable: false, body: (r) => <ProjectCell appId={r.appId} projects={accessibleProjects} /> },
      { field: 'scope', header: 'Scope', sortable: true, body: (r) => scopeLabel(r.scope) },
      {
        field: 'actions',
        header: 'Actions',
        align: 'center',
        hideable: false,
        body: (r) => (
          <div className="flex justify-center gap-1">
            <button
              className="rounded p-2 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              onClick={() => setViewRow(r)}
              aria-label="View"
            >
              <i className="pi pi-eye text-emerald-600" />
            </button>
            <button
              className="rounded p-2 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
              onClick={() => setLinksRow(r)}
              aria-label="Links"
              title="Linked entities"
            >
              <i className="pi pi-sitemap text-indigo-600" />
            </button>
            {canUpdate && (
              <button
                className="rounded p-2 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={() => openEdit(r)}
                aria-label="Edit"
              >
                <i className="pi pi-pencil text-blue-600" />
              </button>
            )}
            {canDelete && (
              <button
                className="rounded p-2 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => setDeleteId(r._id)}
                aria-label="Delete"
              >
                <i className="pi pi-trash text-red-600" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [accessibleProjects, canUpdate, canDelete]
  );

  // Full-page detail view (replaces the old dialog).
  if (viewRow) {
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <DetailShell
          backLabel="Back to Permissions"
          onBack={() => setViewRow(null)}
          title={viewRow.displayName || viewRow.key}
          subtitle={viewRow.key}
          actions={canUpdate ? <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => { const r = viewRow; setViewRow(null); openEdit(r); }} /> : undefined}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <DetailCard title="Permission">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <DetailRow label="Key" value={viewRow.key} mono />
                <DetailRow label="Status" value={<StatusTag status={viewRow.status} />} />
                <DetailRow label="Display name" value={viewRow.displayName || '—'} />
                <DetailRow label="Scope" value={scopeLabel(viewRow.scope)} />
                {viewRow.description && (
                  <div className="col-span-2"><DetailRow label="Description" value={viewRow.description} /></div>
                )}
              </div>
            </DetailCard>
            <DetailCard title="Audit">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {(viewRow as any).createdAt && <DetailRow label="Created" value={formatTimestamp((viewRow as any).createdAt)} />}
                {(viewRow as any).updatedAt && <DetailRow label="Updated" value={formatTimestamp((viewRow as any).updatedAt)} />}
                <div className="col-span-2"><DetailRow label="Permission ID" value={viewRow._id} mono /></div>
              </div>
            </DetailCard>
          </div>
        </DetailShell>
      </>
    );
  }

  // Full-page create / edit form (replaces the old dialog).
  if (dialogOpen) {
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <FormShell
          backLabel="Back to Permissions"
          onBack={() => !saving && setDialogOpen(false)}
          title={editId ? 'Edit Permission' : 'Create Permission'}
          onCancel={() => setDialogOpen(false)}
          onSave={save}
          saving={saving}
          isEdit={!!editId}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Config dropdowns paired on top — a permission is global or project-owned. */}
            <Field label="Project">
              <Dropdown
                value={form.appId || null}
                options={[
                  { label: 'Global (all projects)', value: null },
                  ...accessibleProjects.map((p: AccessibleProject) => ({
                    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                    value: p._id,
                  })),
                ]}
                onChange={(e) => setField('appId', e.value ?? '')}
                placeholder="Select project"
                disabled={!!editId || accessibleProjects.length === 0}
                className="w-full"
              />
            </Field>

            <Field label="Scope">
              <Dropdown value={form.scope} options={SCOPE_OPTIONS} onChange={(e) => setField('scope', e.value)} className="w-full" />
            </Field>

            <Field label="Key" required error={errors.key}>
              <TextField value={form.key} onChange={(v) => setField('key', v)} placeholder="e.g. personnel.read" disabled={!!editId} />
              {editId && <span className="text-xs text-gray-400">Key is immutable once created.</span>}
            </Field>

            <Field label="Display Name" required error={errors.displayName}>
              <TextField value={form.displayName} onChange={(v) => setField('displayName', v)} placeholder="Enter display name" />
            </Field>

            <Field label="Description" className="md:col-span-2">
              <TextAreaField value={form.description} onChange={(v) => setField('description', v)} placeholder="Optional description" />
            </Field>
          </div>
        </FormShell>
      </>
    );
  }

  return (
    <div>
      <Toast ref={toast} position="top-right" />
      <SaasDataTable
        tableId="rbac-permissions"
        columns={columns}
        data={displayRows}
        loading={isFetching}
        dataKey="_id"
        emptyMessage="No permissions found"
        first={first}
        rows={pageSize}
        totalRecords={total}
        onPage={(e: any) => {
          setFirst(e.first);
          setPageSize(e.rows);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={(e: any) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
        filters={filters}
        onFilter={handleFilter}
        globalSearch={search}
        onGlobalSearchChange={(v) => {
          setSearch(v);
          setFirst(0);
        }}
        globalSearchPlaceholder="Search permissions..."
        testId="PermissionsList"
        toolbarStart={
          <div className="flex flex-wrap items-center gap-3">
            {accessibleProjects.length > 1 && (
              <Dropdown
                value={selectedProjectId}
                options={[
                  { label: 'All projects', value: null },
                  ...accessibleProjects.map((p: AccessibleProject) => ({
                    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                    value: p._id,
                  })),
                ]}
                onChange={(e: any) => { setSelectedProjectId(e.value ?? null); setFirst(0); }}
                placeholder="All projects"
                style={{ minWidth: '190px' }}
              />
            )}
            <label className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              <InputSwitch checked={showGlobal} onChange={(e: any) => { setShowGlobal(!!e.value); setFirst(0); }} />
              Show core
            </label>
          </div>
        }
        toolbarEnd={
          <>
            <span className="whitespace-nowrap text-sm text-ink-muted">
              {total} {total === 1 ? 'record' : 'records'}
            </span>
            <RefreshButton
              onClick={() => queryClient.invalidateQueries({ queryKey: ['rbac', 'permissions'] })}
              loading={isFetching}
            />
            {canCreate && <Button label="Create Permission" icon="pi pi-plus" onClick={openCreate} size="small" />}
          </>
        }
      />


      <DependencyDeleteDialog
        entityId={deleteId}
        entityLabel="permission"
        resourceKey="permission"
        getDependencies={permissionsApi.getDependencies}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMut.mutate(deleteId)}
        deleting={deleteMut.isPending}
      />

      <EntityLinksDialog
        entityId={linksRow?._id ?? null}
        title="Permission Links"
        resourceKey="permission"
        getDependencies={permissionsApi.getDependencies}
        onHide={() => setLinksRow(null)}
      />

    </div>
  );
};

export default PermissionsTab;
